import {Component, OnInit, ViewEncapsulation, ViewChild, OnDestroy} from '@angular/core';
import {ToolsService} from '../../shared/services/tools.service';
import {DropzoneComponent, DropzoneConfigInterface} from 'ngx-dropzone-wrapper';
import {UploadCreate} from '../../shared/models/upload.model';
import {DataStorageService} from '../../shared/services/data-storage.service';
import {UploadDescription, UploadService} from '../../shared/services/upload.service';
import {AuthenticationService} from '../../authentication/authentication.service';
import {Subscription, Subject} from 'rxjs';
import {UserLogin} from '../../shared/models/user.model';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'upload-drop-zone',
    templateUrl: './upload-dropzone.html',
    styleUrls: ['./upload-dropzone.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UploadDropZoneComponent implements OnInit, OnDestroy {

    @ViewChild(DropzoneComponent) drpzone: DropzoneComponent;

    // DEBUG flag — toggle runtime logging during development
    private readonly DEBUG_UPLOAD = true;

    public config: DropzoneConfigInterface = {
        // NOTE: url / method / paramName / headers are intentionally NOT set here.
        // They come from DEFAULT_DROPZONE_CONFIG (DROPZONE_CONFIG provider) in
        // global-components.module.ts. Anything declared in this object OVERRIDES
        // the global config for this component.
        //
        // Commit 1fcddba added `url: 'https://server.ozshuttle.com/upload.php'`,
        // which dropped the `/api/upload/` path segment. The POST 404'd, cPanel
        // error pages carry no Access-Control-Allow-Origin header, so the browser
        // blocked the response and Dropzone reported "Server responded with 0 code"
        // instead of a plain 404.
        //
        // The global config also sets `headers: { 'Cache-Control': null }`.
        // That is load-bearing: Cache-Control is NOT a CORS-safelisted request
        // header, so leaving it in triggers an OPTIONS preflight the server does
        // not answer. Do not re-add a `headers` key here.
        parallelUploads: 5,     // send ALL queued files in one processQueue() call
        clickable: true,
        autoProcessQueue: false,
        maxFiles: 5,
        addRemoveLinks: true,
        autoReset: null,
        errorReset: null,
        cancelReset: null
    };

    type: string;
    id: number;
    description: string;

    url: string;

    user: UserLogin;

    private unsubscribe$: Subject<void> = new Subject<void>();

    constructor(private toolsService: ToolsService,
                private dataStorageService: DataStorageService,
                private uploadService: UploadService,
                private authenticationService: AuthenticationService
    ) {
    }

    ngOnInit() {

        this.uploadService.uploadProcessEmitter
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            (data: UploadDescription) => {
                this.type = data.type;
                this.id = data.id;
                this.description = data.description ? data.description : null;
                this.onSubmitDropZone();
            }
        );

        this.authenticationService.user
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            user => {
                this.user = user;
                // If the upload endpoint is protected, attach the JWT here.
                // IMPORTANT: Dropzone uses its own XMLHttpRequest and does NOT pass
                // through Angular HttpInterceptors, so the token is NOT added automatically.
                // if (user && user.token) {
                //     this.config.headers = { Authorization: 'Bearer ' + user.token };
                // }
            });

    }

    onSubmitDropZone() {
        this.drpzone.directiveRef.dropzone().processQueue();
    }

    onSending(data): void {
        if (this.type === 'reservation') {
            data['upload']['filename'] = 'reservation_' + this.id + '_' + data['name'];
        } else if (this.type === 'page') {
            data['upload']['filename'] = 'page_' + this.id + '_' + data['name'];
        } else if (this.type === 'setting') {
            data['upload']['filename'] = 'setting_' + this.id + '_' + data['name'];
        } else if (this.type === 'description') {
            data['upload']['filename'] = 'description_' + data['name'];
        }
        if (this.DEBUG_UPLOAD) {
            console.log('[DEBUG_UPLOAD] sending file as:', data['upload']['filename']);
        }
    }

    onUploadSuccess(args: any): void {
        console.log('onUploadSuccess:', args);

        if (args['error']) {
            alert(args['error']);
        } else {
            const name = args[0]['upload']['filename'];
            const type = args[0]['type'];
            if (this.type === 'reservation') {
                this.url = 'https://server.ozshuttle.com/uploads/reservation/' + name;
            } else if (this.type === 'page') {
                this.url = 'https://server.ozshuttle.com/uploads/page/' + name;
            } else if (this.type === 'setting') {
                this.url = 'https://server.ozshuttle.com/uploads/setting/' + name;
            } else if (this.type === 'description') {
                this.url = 'https://server.ozshuttle.com/uploads/description/' + name;
            }
            const upload = new UploadCreate(this.user.id, name, type, this.url);
            if (this.type === 'reservation') {
                upload.reservation_id = this.id;
            } else if (this.type === 'page') {
                upload.page_id = this.id;
            } else if (this.type === 'setting') {
                upload.setting_id = this.id;
            } else if (this.type === 'description') {
                upload.description = this.description;
            }

            console.log(upload);

            this.dataStorageService.createUpload(upload)
                .pipe(takeUntil(this.unsubscribe$)).subscribe(
                responseData => {
                    this.uploadService.uploadSub.next(responseData['upload']);
                }
            );
        }

    }

    onError(args) {
        // ngx-dropzone-wrapper emits [file, errorMessage, xhr] for the error event
        console.error('[DEBUG_UPLOAD] dropzone error:', args);
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

}
